/**
 * Rate Limiter simple para prevenir ataques de fuerza bruta
 * ⚠️ NOTA: Este es un rate limiter básico del lado del cliente.
 * Para producción, se debe implementar rate limiting en el backend.
 */

interface RateLimitEntry {
  count: number
  resetTime: number
}

const rateLimitStore = new Map<string, RateLimitEntry>()

/**
 * Configuración de rate limiting
 */
const RATE_LIMIT_CONFIG = {
  // Máximo de intentos permitidos
  maxAttempts: 5,
  // Tiempo de ventana en milisegundos (15 minutos)
  windowMs: 15 * 60 * 1000,
  // Tiempo de bloqueo después de exceder el límite (30 minutos)
  lockoutMs: 30 * 60 * 1000,
}

/**
 * Verifica si una acción está permitida según el rate limit
 */
export function checkRateLimit(key: string): { allowed: boolean; remainingAttempts: number; resetTime: number | null } {
  const now = Date.now()
  const entry = rateLimitStore.get(key)

  if (!entry) {
    // Primera vez, permitir
    rateLimitStore.set(key, {
      count: 1,
      resetTime: now + RATE_LIMIT_CONFIG.windowMs,
    })
    return {
      allowed: true,
      remainingAttempts: RATE_LIMIT_CONFIG.maxAttempts - 1,
      resetTime: now + RATE_LIMIT_CONFIG.windowMs,
    }
  }

  // Si el tiempo de bloqueo ha pasado, resetear
  if (entry.resetTime < now) {
    rateLimitStore.set(key, {
      count: 1,
      resetTime: now + RATE_LIMIT_CONFIG.windowMs,
    })
    return {
      allowed: true,
      remainingAttempts: RATE_LIMIT_CONFIG.maxAttempts - 1,
      resetTime: now + RATE_LIMIT_CONFIG.windowMs,
    }
  }

  // Si excedió el límite, verificar si está en período de bloqueo
  if (entry.count >= RATE_LIMIT_CONFIG.maxAttempts) {
    const lockoutEnd = entry.resetTime + RATE_LIMIT_CONFIG.lockoutMs
    if (now < lockoutEnd) {
      return {
        allowed: false,
        remainingAttempts: 0,
        resetTime: lockoutEnd,
      }
    } else {
      // Bloqueo terminado, resetear
      rateLimitStore.set(key, {
        count: 1,
        resetTime: now + RATE_LIMIT_CONFIG.windowMs,
      })
      return {
        allowed: true,
        remainingAttempts: RATE_LIMIT_CONFIG.maxAttempts - 1,
        resetTime: now + RATE_LIMIT_CONFIG.windowMs,
      }
    }
  }

  // Incrementar contador
  entry.count++
  rateLimitStore.set(key, entry)

  return {
    allowed: true,
    remainingAttempts: RATE_LIMIT_CONFIG.maxAttempts - entry.count,
    resetTime: entry.resetTime,
  }
}

/**
 * Registra un intento fallido
 */
export function recordFailedAttempt(key: string): void {
  const now = Date.now()
  const entry = rateLimitStore.get(key)

  if (!entry) {
    rateLimitStore.set(key, {
      count: 1,
      resetTime: now + RATE_LIMIT_CONFIG.windowMs,
    })
  } else {
    entry.count++
    rateLimitStore.set(key, entry)
  }
}

/**
 * Limpia los intentos exitosos (resetear contador)
 */
export function clearRateLimit(key: string): void {
  rateLimitStore.delete(key)
}

/**
 * Obtiene información del rate limit actual
 */
export function getRateLimitInfo(key: string): { count: number; resetTime: number | null; isLocked: boolean } {
  const entry = rateLimitStore.get(key)
  if (!entry) {
    return { count: 0, resetTime: null, isLocked: false }
  }

  const now = Date.now()
  const isLocked = entry.count >= RATE_LIMIT_CONFIG.maxAttempts && now < (entry.resetTime + RATE_LIMIT_CONFIG.lockoutMs)

  return {
    count: entry.count,
    resetTime: entry.resetTime,
    isLocked,
  }
}


